import { memo, useMemo } from "react";
import { gridLines, sectorLabels, type GridConfig } from "@/engine/grid.ts";
import { gameToScreen, type Camera, type ProjectionConfig } from "@/engine/projection.ts";
import type { Bounds } from "@/engine/coords.ts";

interface GridLayerProps {
  readonly grid: GridConfig;
  readonly playable: Bounds;
  readonly camera: Camera;
  readonly config: ProjectionConfig;
  readonly showLabels: boolean;
}

/**
 * The callout grid.
 *
 * Sector labels do not scale with the map: at 16 km wide, text that zooms
 * becomes either invisible or enormous, and neither is readable at a glance.
 * They are drawn at a fixed size and simply repositioned.
 *
 * The playable boundary is drawn too, because a large part of the 16 km render
 * is terrain you cannot fight on, and mistaking one for the other wastes a
 * whole rotation.
 */
function GridLayerImpl({
  grid,
  playable,
  camera,
  config,
  showLabels,
}: GridLayerProps): React.ReactElement {
  const lines = useMemo(() => gridLines(grid), [grid]);

  const projected = useMemo(() => {
    const verticals = lines.xs.map((x) => ({
      key: x,
      top: gameToScreen({ x, y: grid.bounds.maxY }, camera, config),
      bottom: gameToScreen({ x, y: grid.bounds.minY }, camera, config),
    }));
    const horizontals = lines.ys.map((y) => ({
      key: y,
      left: gameToScreen({ x: grid.bounds.minX, y }, camera, config),
      right: gameToScreen({ x: grid.bounds.maxX, y }, camera, config),
    }));
    return { verticals, horizontals };
  }, [lines, grid.bounds, camera, config]);

  const labels = useMemo(() => {
    if (!showLabels) return [];
    return sectorLabels(grid).map((entry) => ({
      label: entry.label,
      at: gameToScreen(entry.at, camera, config),
    }));
  }, [grid, camera, config, showLabels]);

  const boundary = useMemo(() => {
    const nw = gameToScreen({ x: playable.minX, y: playable.maxY }, camera, config);
    const se = gameToScreen({ x: playable.maxX, y: playable.minY }, camera, config);
    return { x: nw.x, y: nw.y, width: se.x - nw.x, height: se.y - nw.y };
  }, [playable, camera, config]);

  return (
    <svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full">
      <g stroke="oklch(0.95 0.005 250 / 0.16)" strokeWidth={1}>
        {projected.verticals.map((line) => (
          <line
            key={`v${line.key}`}
            x1={line.top.x}
            y1={line.top.y}
            x2={line.bottom.x}
            y2={line.bottom.y}
          />
        ))}
        {projected.horizontals.map((line) => (
          <line
            key={`h${line.key}`}
            x1={line.left.x}
            y1={line.left.y}
            x2={line.right.x}
            y2={line.right.y}
          />
        ))}
      </g>

      <rect
        x={boundary.x}
        y={boundary.y}
        width={Math.max(0, boundary.width)}
        height={Math.max(0, boundary.height)}
        fill="none"
        stroke="oklch(0.78 0.15 85 / 0.5)"
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />

      {labels.map((entry) => (
        <text
          key={entry.label}
          x={entry.at.x}
          y={entry.at.y}
          textAnchor="middle"
          dominantBaseline="central"
          fill="oklch(0.95 0.005 250 / 0.3)"
          fontSize={13}
          fontFamily="var(--font-mono)"
          fontWeight={600}
        >
          {entry.label}
        </text>
      ))}
    </svg>
  );
}

export const GridLayer = memo(GridLayerImpl);
